'use client';

import type { DingTongItem } from '@/api/types/ding-tong';
import { type JumpTarget, useJump } from '@/components/MagicJump';
import ClientOnly from '@/components/Responsive/ClientOnly';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import logger from '@/utils/client-logger';
import { getRouteCnName } from '@/utils/route-metadata';
import { setCampaignEntrancePageScene } from '@/utils/tracking/entrancePageScene';
import {
  Children,
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { InView } from 'react-intersection-observer';
import type { HeaderProps } from '..';
import { PcTopBarCountDown } from './CountDown';
import styles from './DingTong.module.css';
import {
  getCountdownDisplay,
  getDingTongJumpContent,
  getDingTongTrackingJumpType,
  getNextDingTongBoundaryDelay,
  hasDingTongJumpTarget,
  hasDingTongShopNowText,
  hasValidDingTongJumpTarget,
  resolveDingTong,
  type ResolvedDingTong,
} from './ding-tong';

const MAX_TIMEOUT_MS = 2_147_000_000;
export const PC_TOP_BAR_ROTATION_INTERVAL_MS = 3000;
const PC_TOP_BAR_TRANSITION_MS = 300;
const PC_TOP_BAR_PROMOTION_GAP_PX = 24;

export function getVisibleTopBarPromotionCount(containerWidth: number, itemWidths: number[]) {
  if (itemWidths.length === 0 || itemWidths[0] > containerWidth) return 0;

  let visibleCount = 1;
  let occupiedWidth = itemWidths[0];
  for (let index = 1; index < itemWidths.length; index += 1) {
    const nextOccupiedWidth = occupiedWidth + PC_TOP_BAR_PROMOTION_GAP_PX + itemWidths[index];
    if (nextOccupiedWidth > containerWidth) break;
    occupiedWidth = nextOccupiedWidth;
    visibleCount += 1;
  }
  return visibleCount;
}

function ResponsivePromotionGroup({ children }: { children: ReactNode }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const promotionItems = Children.toArray(children);
  const [visibleCount, setVisibleCount] = useState(() => Math.min(1, promotionItems.length));

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const itemElements = Array.from(
      container.querySelectorAll<HTMLElement>(':scope > [data-top-bar-promotion-item]'),
    );

    const updateVisibleCount = () => {
      const nextVisibleCount = getVisibleTopBarPromotionCount(
        container.clientWidth,
        itemElements.map((item) => item.scrollWidth),
      );
      setVisibleCount((currentCount) =>
        currentCount === nextVisibleCount ? currentCount : nextVisibleCount,
      );
    };

    updateVisibleCount();
    const resizeObserver = new ResizeObserver(updateVisibleCount);
    resizeObserver.observe(container);
    itemElements.forEach((item) => resizeObserver.observe(item));
    return () => resizeObserver.disconnect();
  }, [promotionItems.length]);

  return (
    <div
      ref={containerRef}
      className="relative flex h-full min-w-0 flex-1 items-center gap-[24px] overflow-hidden"
      data-top-bar-promotion-group
    >
      {promotionItems.map((item, index) => {
        const isVisible = index < visibleCount;
        return (
          <div
            key={`top-bar-promotion-${index}`}
            className={`w-max shrink-0 ${isVisible ? '' : 'pointer-events-none invisible absolute'}`}
            data-top-bar-promotion-item
            data-visible={isVisible}
            aria-hidden={!isVisible}
            inert={!isVisible}
          >
            {item}
          </div>
        );
      })}
    </div>
  );
}

function ScrollingText({ text }: { text: string }) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [scrollDistance, setScrollDistance] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    const content = textRef.current;
    if (!container || !content) return;

    const measure = () => {
      setScrollDistance(Math.max(0, content.scrollWidth - container.clientWidth));
    };

    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(container);
    resizeObserver.observe(content);
    return () => resizeObserver.disconnect();
  }, [text]);

  const marqueeStyle = {
    '--ding-tong-scroll-distance': `${-scrollDistance}px`,
    '--ding-tong-scroll-duration': `${Math.max(4, scrollDistance / 30)}s`,
  } as CSSProperties;

  return (
    <span ref={containerRef} className="block max-w-[60vw] overflow-hidden whitespace-nowrap">
      <span
        ref={textRef}
        className={`inline-block whitespace-nowrap ${scrollDistance > 0 ? styles.marqueeText : ''}`}
        style={marqueeStyle}
      >
        {text}
      </span>
    </span>
  );
}

function PcDingTongSlide({
  item,
  now,
  receivedAt,
  resolved,
  onClockChange,
  trackExposure = true,
}: {
  item: DingTongItem;
  now: number;
  receivedAt: number;
  resolved: ResolvedDingTong;
  onClockChange: () => void;
  trackExposure?: boolean;
}) {
  const { trackEvent } = useReporting();
  const toJumpTarget = useJump();
  const countdownDisplay = useMemo(
    () => getCountdownDisplay(item, now, receivedAt),
    [item, now, receivedAt],
  );
  const showShopNow = hasDingTongShopNowText(item);
  const isClickable = hasDingTongJumpTarget(item);
  const jumpContent = getDingTongJumpContent(item);
  const trackingJumpType = getDingTongTrackingJumpType(item);

  const handleClick = () => {
    if (!isClickable) return;

    if (!hasValidDingTongJumpTarget(item)) {
      logger.error('[DingTong] invalid jump target', {
        itemId: item.id,
        page: resolved.page,
        jumpType: item.jump_type,
        jumpTarget: item.Jump_target,
      });
      return;
    }

    setCampaignEntrancePageScene('顶通', String(item.Jump_target || ''));
    trackEvent(
      {
        eventName: 'DingTong_Click',
        properties: {
          page_name: getRouteCnName(location.pathname),
          jump_type: trackingJumpType,
          jump_content: jumpContent,
        },
      },
      [ProviderType.SENSORS],
    );
    try {
      toJumpTarget(item as JumpTarget);
    } catch (error) {
      logger.error('[DingTong] jump failed', error, {
        itemId: item.id,
        page: resolved.page,
        jumpType: item.jump_type,
        jumpTarget: item.Jump_target,
      });
    }
  };

  const handleExposure = (inView: boolean) => {
    if (!inView || !trackExposure) return;
    trackEvent(
      {
        eventName: 'DingTong_Exp',
        properties: {
          page_name: getRouteCnName(location.pathname),
          jump_type: trackingJumpType,
          jump_content: jumpContent,
        },
      },
      [ProviderType.SENSORS],
    );
  };

  const content = (
    <div className="flex h-[40px] min-w-0 items-center font-br text-[12px] font-normal leading-[14px] text-white">
      <ScrollingText text={item.dt_text || ''} />
      {showShopNow ? (
        <span className="ml-[40px] shrink-0 underline">{item.shop_now_text}</span>
      ) : null}
      {countdownDisplay ? (
        <span className="ml-[40px] shrink-0">
          <ClientOnly>
            <PcTopBarCountDown
              time={countdownDisplay.remainingSeconds}
              textColor={countdownDisplay.textColor}
              numberColor={countdownDisplay.numberColor}
              onCountDownEnd={onClockChange}
            />
          </ClientOnly>
        </span>
      ) : null}
    </div>
  );

  return (
    <InView
      key={`${resolved.page}-${item.id ?? ''}-${item.dt_text ?? ''}`}
      triggerOnce
      onChange={handleExposure}
    >
      {isClickable ? (
        <button
          type="button"
          className="block h-[40px] w-full cursor-pointer border-0 bg-transparent p-0 text-left"
          onClick={handleClick}
        >
          {content}
        </button>
      ) : (
        content
      )}
    </InView>
  );
}

interface PcTopBarCarouselProps {
  children: ReactNode;
  dtInfo: HeaderProps['dtInfo'];
  hasPromotion: boolean;
  showDingTong: boolean;
}

/** PC Top Bar 左侧专用轮播；移动端继续使用原 DingTong 组件。 */
export default function PcTopBarCarousel({
  children,
  dtInfo,
  hasPromotion,
  showDingTong,
}: PcTopBarCarouselProps) {
  const isShowDtBanner = useStore((state) => state.isShowDtBanner);
  const { pathname } = useRouteMetadata();
  const responseClockRef = useRef({ config: dtInfo, receivedAt: Date.now() });
  if (responseClockRef.current.config !== dtInfo) {
    responseClockRef.current = { config: dtInfo, receivedAt: Date.now() };
  }

  const [now, setNow] = useState(() => Date.now());
  const receivedAt = responseClockRef.current.receivedAt;
  const resolved = useMemo(
    () => (showDingTong ? resolveDingTong(dtInfo, pathname, now, receivedAt) : null),
    [dtInfo, now, pathname, receivedAt, showDingTong],
  );
  const dingTongItems = isShowDtBanner ? resolved?.items || [] : [];
  const itemsKey = dingTongItems
    .map((item, index) => `${item.id ?? index}:${item.deadline_time_TUC_5_unix ?? ''}`)
    .join('|');
  const slidesKey = [resolved?.page ?? '', itemsKey, Number(hasPromotion)].join('|');
  const slideCount = dingTongItems.length + Number(hasPromotion);
  const [carouselState, setCarouselState] = useState({
    key: slidesKey,
    index: 0,
    transitionEnabled: true,
  });
  const [isAutoRotationPaused, setIsAutoRotationPaused] = useState(false);
  const activeIndex = carouselState.key === slidesKey ? carouselState.index : 0;
  const transitionEnabled =
    carouselState.key === slidesKey ? carouselState.transitionEnabled : true;
  const logicalActiveIndex = activeIndex === slideCount ? 0 : activeIndex;
  const currentItem = dingTongItems[logicalActiveIndex];
  const firstDingTongItem = dingTongItems[0];

  useEffect(() => {
    setNow(Date.now());
  }, [dtInfo, pathname, showDingTong]);

  useEffect(() => {
    if (slideCount <= 1 || isAutoRotationPaused) return;
    const timer = window.setInterval(() => {
      setNow(Date.now());
      setCarouselState((currentState) => {
        const currentIndex = currentState.key === slidesKey ? currentState.index : 0;
        return {
          key: slidesKey,
          index: Math.min(currentIndex + 1, slideCount),
          transitionEnabled: true,
        };
      });
    }, PC_TOP_BAR_ROTATION_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [isAutoRotationPaused, slideCount, slidesKey]);

  useEffect(() => {
    if (!dtInfo || !resolved) return;
    const delay = getNextDingTongBoundaryDelay(dtInfo, currentItem, now, receivedAt);
    if (delay === null) return;
    const timer = window.setTimeout(
      () => setNow(Date.now()),
      Math.min(MAX_TIMEOUT_MS, Math.max(50, delay + 25)),
    );
    return () => window.clearTimeout(timer);
  }, [currentItem, dtInfo, now, receivedAt, resolved]);

  const beginLoopReset = useCallback(() => {
    setCarouselState((currentState) => {
      if (currentState.key !== slidesKey || currentState.index !== slideCount) {
        return currentState;
      }
      return {
        ...currentState,
        transitionEnabled: false,
      };
    });
  }, [slideCount, slidesKey]);

  useEffect(() => {
    if (slideCount <= 1 || activeIndex !== slideCount) return;
    const timer = window.setTimeout(beginLoopReset, PC_TOP_BAR_TRANSITION_MS + 50);
    return () => window.clearTimeout(timer);
  }, [activeIndex, beginLoopReset, slideCount]);

  useEffect(() => {
    if (transitionEnabled) return;
    const frame = window.requestAnimationFrame(() => {
      setCarouselState((currentState) => {
        if (currentState.key !== slidesKey || currentState.transitionEnabled) {
          return currentState;
        }
        if (currentState.index === slideCount) {
          return { ...currentState, index: 0 };
        }
        return { ...currentState, transitionEnabled: true };
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [slideCount, slidesKey, transitionEnabled, activeIndex]);

  const handleClockChange = useCallback(() => setNow(Date.now()), []);

  return (
    <li
      className="h-full min-w-0 flex-1 overflow-hidden"
      data-active-slide-index={activeIndex}
      data-slide-count={slideCount}
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') setIsAutoRotationPaused(true);
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'mouse') setIsAutoRotationPaused(false);
      }}
    >
      {slideCount > 0 ? (
        <div
          className={transitionEnabled ? 'transition-transform duration-300 ease-out' : ''}
          data-carousel-track
          style={{ transform: `translateY(-${activeIndex * 40}px)` }}
          onTransitionEnd={(event) => {
            if (event.target === event.currentTarget && event.propertyName === 'transform') {
              beginLoopReset();
            }
          }}
        >
          {dingTongItems.map((item, index) => (
            <div
              key={`ding-tong-${item.id ?? index}-${item.dt_text ?? ''}`}
              aria-hidden={index !== activeIndex}
              inert={index !== activeIndex}
            >
              {resolved ? (
                <PcDingTongSlide
                  item={item}
                  now={now}
                  receivedAt={receivedAt}
                  resolved={resolved}
                  onClockChange={handleClockChange}
                />
              ) : null}
            </div>
          ))}
          {hasPromotion ? (
            <div
              className="flex h-[40px] min-w-0 items-center"
              aria-hidden={activeIndex !== dingTongItems.length}
              inert={activeIndex !== dingTongItems.length}
            >
              <ResponsivePromotionGroup>{children}</ResponsivePromotionGroup>
            </div>
          ) : null}
          {slideCount > 1 && firstDingTongItem && resolved ? (
            <div
              data-loop-clone="first"
              aria-hidden={activeIndex !== slideCount}
              inert={activeIndex !== slideCount}
            >
              <PcDingTongSlide
                item={firstDingTongItem}
                now={now}
                receivedAt={receivedAt}
                resolved={resolved}
                onClockChange={handleClockChange}
                trackExposure={false}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
